import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { Button } from "../ui/button";

const EASE_OUT = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, ease: EASE_OUT, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroSection = ({ onStartQuiz }) => {
  const sectionRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), {
    stiffness: 120,
    damping: 18,
  });

  const handleMouseMove = (e) => {
    const rect = sectionRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const fadeUp = (delay) => ({
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay },
  });

  return (
    <section
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20 lg:pt-24"
      data-testid="hero-section"
    >
      {/* Backdrop: obsidian grid + ambient voltage glows */}
      <div className="absolute inset-0 grid-pattern opacity-70" />
      <div className="absolute -top-48 -left-48 w-[36rem] h-[36rem] rounded-full bg-[#00E5FF]/[0.07] blur-[140px]" />
      <div className="absolute -bottom-24 -right-24 w-[32rem] h-[32rem] rounded-full bg-[#0284C7]/[0.08] blur-[130px]" />

      <div className="relative z-10 px-6 md:px-12 lg:px-24 w-full max-w-7xl mx-auto grid xl:grid-cols-[1.25fr_0.85fr] gap-16 xl:gap-10 items-center">
        {/* Left — kinetic copy */}
        <div className="max-w-2xl">
          <motion.div {...fadeUp(0.05)}>
            <span className="mono-label" data-testid="hero-label">
              AI-Powered Executive Intelligence
            </span>
          </motion.div>

          {/* Masked line-by-line headline reveal */}
          <h1
            className="mt-7 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-white font-['Outfit']"
            data-testid="hero-headline"
          >
            <MaskedLine delay={0.15}>AI That Pinpoints the</MaskedLine>
            <MaskedLine delay={0.27}>Decisions Leaders</MaskedLine>
            <MaskedLine delay={0.39} className="gradient-text">
              Need to Make.
            </MaskedLine>
          </h1>

          <motion.p
            {...fadeUp(0.55)}
            className="mt-7 text-base md:text-lg text-[#A1A1AA] max-w-xl leading-relaxed"
            data-testid="hero-subtext"
          >
            OnTenacity reads your existing systems, reports, and business updates
            to show what changed, what matters, and where leadership needs to act.
          </motion.p>

          <motion.div {...fadeUp(0.9)} className="mt-6">
            <Button
              onClick={onStartQuiz}
              className="btn-primary px-8 py-4 rounded-full text-base font-semibold flex items-center gap-2 group shadow-[0_0_25px_rgba(0,229,255,0.25)] hover:shadow-[0_0_40px_rgba(0,229,255,0.45)] transition-shadow duration-300"
              data-testid="hero-start-quiz-btn"
            >
              Discover my company's AI Persona
              <ArrowRight
                size={20}
                className="transition-transform group-hover:translate-x-1"
              />
            </Button>
          </motion.div>

          <motion.p
            {...fadeUp(1)}
            className="mt-6 text-xs text-[#71717A] max-w-md leading-relaxed"
          >
            Your result suggests a starting approach — not a score of your
            company's potential.
          </motion.p>
        </div>

        {/* Right — HUD-framed Earth visual with 3D cursor parallax */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 32 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, ease: EASE_OUT, delay: 0.5 }}
          className="relative [perspective:1400px]"
        >
          {/* Ambient glow behind frame */}
          <div className="absolute inset-0 -m-8 rounded-full bg-[#00E5FF]/[0.08] blur-[80px]" />

          <motion.div
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="hud-frame relative rounded-xl border border-[#00E5FF]/20 bg-[#0D1527]/60 p-2 backdrop-blur-md shadow-[0_0_50px_rgba(0,229,255,0.10)]"
            data-testid="hero-visual-frame"
          >
            {/* Remaining corner brackets (top-right / bottom-left) */}
            <span className="absolute -top-2 -right-2 w-[22px] h-[22px] border-t-2 border-r-2 border-[#00E5FF]/80 z-10" />
            <span className="absolute -bottom-2 -left-2 w-[22px] h-[22px] border-b-2 border-l-2 border-[#00E5FF]/80 z-10" />

            <div className="relative overflow-hidden rounded-lg">
              <motion.img
                src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1400&q=80&auto=format&fit=crop"
                alt="Earth at night — the connected enterprise seen from orbit"
                className="w-full max-h-[420px] lg:max-h-[560px] object-cover"
                animate={{ scale: [1, 1.06, 1] }}
                transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
                loading="eager"
              />
              {/* Readability + tone overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/70 via-transparent to-[#030712]/20" />

              {/* Slow scanline sweep */}
              <motion.div
                className="absolute left-0 right-0 h-24 bg-gradient-to-b from-transparent via-[#00E5FF]/[0.08] to-transparent"
                animate={{ top: ["-25%", "125%"] }}
                transition={{ duration: 7, repeat: Infinity, ease: "linear", repeatDelay: 4 }}
              />
            </div>

            {/* HUD metadata chips — floating on the z plane */}
            <div
              className="absolute top-5 left-5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#00E5FF]/90 bg-[#030712]/70 backdrop-blur-sm border border-[#00E5FF]/25 rounded px-2.5 py-1.5"
              style={{ transform: "translateZ(50px)" }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-pulse" />
              Live · Enterprise Context
            </div>
            <div
              className="absolute bottom-5 right-5 font-mono text-[10px] uppercase tracking-[0.25em] text-white/70 bg-[#030712]/70 backdrop-blur-sm border border-white/15 rounded px-2.5 py-1.5"
              style={{ transform: "translateZ(40px)" }}
            >
              Attribution 99.9%
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
